import React, { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { ADDONS, ADDON_CATALOG, isAddonEnabled } from '../utils/addons';
import { usePlatformOwner } from '../hooks/usePlatformOwner';

const LIMITS = [
  ['monthlyBudgetUsd', 'Monthly budget (USD)', '0.01'],
  ['dailyTokenCap', 'Daily token cap', '1'],
  ['maxOutputTokens', 'Maximum output tokens per request', '1'],
  ['onDemandPerUserPerHour', 'Refreshes per user per hour', '1']
];
const ROLLING_FIELDS = [
  ['llmRollingActiveMonths', 'Active customers: ordered within (months, 1-36)', 1, 36],
  ['llmRollingMaxCustomersPerRun', 'Maximum customers per night (1-100)', 1, 100],
  ['llmRollingDailyTokenSharePct', 'Share of daily token cap for rolling refresh (%, 10-100)', 10, 100]
];
const CYCLE_OPTIONS = [[7, 'Weekly (7 days)'], [14, 'Every two weeks (14 days)'], [30, 'Monthly (30 days)'],
  [60, 'Every two months (60 days)'], [90, 'Quarterly (90 days)']];
const SETTING_KEYS = [...LIMITS.map(([key]) => key), 'llmEnabled', 'llmNightlyEnabled', 'llmCustomerScope',
  'decisionBriefEnabled', 'qualificationEnabled', 'quotingEnabled', 'quoteAiEnabled', 'llmRollingEnabled', 'llmRollingCycleDays', ...ROLLING_FIELDS.map(([key]) => key)];
const usd = value => Number.isFinite(value)
  ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 6 })
  : 'Unavailable';
const count = value => Number.isFinite(value) ? value.toLocaleString('en-US') : 'Unavailable';
const message = error => `${error.code ? `${error.code}: ` : ''}${error.message || 'Request failed.'}`;
const remaining = (limit, used, reserved) => [limit, used, reserved].every(Number.isFinite)
  ? Math.max(0, limit - used - reserved) : undefined;

function Utilization({ label, used, reserved, limit, money = false }) {
  const format = money ? usd : count;
  const available = remaining(limit, used, reserved);
  return (
    <section className="border rounded-lg p-4 min-w-0">
      <h3 className="font-semibold">{label}</h3>
      <p>Used: {format(used)} / {format(limit)}</p>
      <p>Reserved: {format(reserved)}</p>
      <p>Remaining after reservations: {format(available)}</p>
      {available !== undefined && limit > 0 && (
        <progress aria-label={`${label} utilization`} max={limit} value={Math.min(limit, used + reserved)} className="w-full" />
      )}
    </section>
  );
}

function RollingStatus({ rolling }) {
  const run = rolling.lastRun;
  const short = Number.isFinite(rolling.capacityPerNight) && rolling.capacityPerNight < rolling.quotaPerNight;
  return (
    <section aria-label="Rolling refresh status" className="text-sm space-y-1">
      <h3 className="font-semibold">Coverage (saved settings)</h3>
      <p>Active customers (last order on or after {rolling.cutoff}): {count(rolling.eligible)}</p>
      <p>Refreshed within the cycle: {count(rolling.current)}; due: {count(rolling.due)}; never summarized: {count(rolling.neverSummarized)}</p>
      <p>Customers per night: {count(rolling.quotaPerNight)}
        {rolling.steadyStatePerNight > rolling.quotaPerNight && ` (cycle needs ${count(rolling.steadyStatePerNight)}; raise the nightly maximum)`}</p>
      <p>Rolling token allowance per day: {count(rolling.tokenCap)}</p>
      {rolling.sampleSize > 0 ? (
        <>
          <p>Estimated per customer: {usd(rolling.avgCostUsd)}, about {count(Math.round(rolling.avgBilledTokens))} billed tokens
            (from {rolling.sampleSize} recent successful summaries); each call first reserves about {count(rolling.reservationTokens)} tokens.</p>
          <p>Estimated cost per full cycle: {usd(rolling.estimatedCostPerCycleUsd)}; per 30 days: {usd(rolling.estimatedMonthlyCostUsd)}</p>
          <p>Estimated customers that fit in the rolling allowance per night: {count(rolling.capacityPerNight)}</p>
        </>
      ) : <p>Cost estimate unavailable until recent successful summaries exist.</p>}
      {short && (
        <p role="status" className="text-amber-900">
          The rolling token allowance fits about {count(rolling.capacityPerNight)} customers per night, below the {count(rolling.quotaPerNight)} needed.
          Raise the daily token cap or the rolling share, or the cycle will take longer than configured.
        </p>
      )}
      <h3 className="font-semibold pt-2">Last rolling run</h3>
      {run ? (
        <>
          <p><time>{run.startedAt}</time> — {run.stopReason}</p>
          <p>Attempted: {count(run.attempted)} of {count(run.quota)}; examined: {count(run.examined)}; due before run: {count(run.dueBefore)}</p>
          <p>Outcomes: {Object.entries(run.counts || {}).map(([status, n]) => `${status} ${n}`).join(', ') || 'none'}</p>
        </>
      ) : <p>No rolling run yet.</p>}
    </section>
  );
}

export default function PlatformControlsPage() {
  const { isOwner, loading: ownerLoading, error: ownerError } = usePlatformOwner();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [tempTokens, setTempTokens] = useState('100000');
  const [tempHours, setTempHours] = useState('24');
  const revision = useRef(0);
  const lifecycle = useRef(0);
  const invalidateRequests = useCallback(() => {
    revision.current++;
    lifecycle.current++;
  }, []);

  const load = useCallback(async () => {
    const request = ++revision.current;
    setLoading(true);
    setError('');
    setData(null);
    setDraft(null);
    setReason('');
    setConfirmed(false);
    try {
      const result = await httpsCallable(functions, 'getPlatformControls')({});
      if (request !== revision.current) return;
      const next = result.data;
      if (!next?.tenantId || !next.settings || !next.addons || !next.budget || !next.pricing ||
          !Array.isArray(next.recentUsage) || !Array.isArray(next.recentAudit)) {
        throw new Error('Incomplete platform controls response.');
      }
      setData(next);
      setDraft({
        addons: Object.fromEntries(ADDON_CATALOG.map(addon => [addon.key, isAddonEnabled(next.addons, addon.key)])),
        settings: {
          ...Object.fromEntries(SETTING_KEYS.map(key => [key, next.settings[key]])),
          llmPilotCustomerIds: (next.settings.llmPilotCustomerIds || []).join(', '),
          llmNightlyMaxCustomers: next.settings.llmNightlyMaxCustomers ?? ''
        }
      });
    } catch (err) {
      if (request === revision.current) setError(message(err));
    } finally {
      if (request === revision.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner && !ownerLoading && !ownerError) load();
    else {
      setData(null);
      setDraft(null);
    }
    setReason('');
    setConfirmed(false);
    setNotice('');
    setSaving(false);
    return invalidateRequests;
  }, [isOwner, ownerLoading, ownerError, load, invalidateRequests]);

  const setSetting = (key, value) => {
    setConfirmed(false);
    setDraft(current => ({
      ...current,
      settings: {
        ...current.settings, [key]: value,
        ...(key === 'llmEnabled' && !value ? { llmNightlyEnabled: false, llmRollingEnabled: false } : {})
      }
    }));
  };

  const toggleAddon = (key, enabled) => {
    setConfirmed(false);
    setDraft(current => ({
      addons: {
        ...current.addons,
        [key]: enabled,
        ...(key === ADDONS.AI_SALES_AGENT && !enabled ? { [ADDONS.AI_SALES_AGENT_PAID]: false } : {})
      },
      settings: {
        ...current.settings,
        ...(key === ADDONS.AI_SALES_AGENT && !enabled ? { llmEnabled: false, llmNightlyEnabled: false, llmRollingEnabled: false } : {}),
        ...(key === ADDONS.AI_SALES_AGENT_PAID ? {
          llmEnabled: enabled, llmNightlyEnabled: false, llmRollingEnabled: false,
          ...(enabled ? { llmCustomerScope: 'all' } : {})
        } : {})
      }
    }));
  };

  const save = async event => {
    event.preventDefault();
    if (!isOwner || saving || !draft) return;
    setError('');
    setNotice('');
    if (!reason.trim() || !confirmed) {
      setError('A change reason and explicit paid-cost confirmation are required for every save.');
      return;
    }
    const settings = { ...draft.settings };
    if (typeof settings.decisionBriefEnabled !== 'boolean') delete settings.decisionBriefEnabled;
    if (typeof settings.qualificationEnabled !== 'boolean') delete settings.qualificationEnabled;
    if (typeof settings.quotingEnabled !== 'boolean') delete settings.quotingEnabled;
    if (typeof settings.quoteAiEnabled !== 'boolean') delete settings.quoteAiEnabled;
    if (draft.addons[ADDONS.AI_SALES_AGENT_PAID] && !isAddonEnabled(data.addons, ADDONS.AI_SALES_AGENT_PAID)) {
      Object.assign(settings, { llmEnabled: true, llmNightlyEnabled: false, llmRollingEnabled: false, llmCustomerScope: 'all' });
    }
    const rollingRanges = [['llmRollingCycleDays', 'Refresh cycle (days)', 7, 90], ...ROLLING_FIELDS];
    if (typeof settings.llmRollingEnabled !== 'boolean') delete settings.llmRollingEnabled;
    for (const [key, label, min, max] of rollingRanges) {
      if (settings[key] === undefined || settings[key] === null || settings[key] === '') {
        if (settings.llmRollingEnabled) {
          setError(`${label} is required to enable rolling refresh.`);
          return;
        }
        delete settings[key];
        continue;
      }
      const value = Number(settings[key]);
      if (!Number.isInteger(value) || value < min || value > max) {
        setError(`${label} must be a whole number from ${min}-${max}.`);
        return;
      }
      settings[key] = value;
    }
    if (settings.llmRollingEnabled && !settings.llmEnabled) {
      setError('Rolling refresh requires paid LLM generation.');
      return;
    }
    for (const [key, label] of LIMITS) {
      const value = Number(settings[key]);
      if (!Number.isFinite(value) || value <= 0 || (key !== 'monthlyBudgetUsd' && !Number.isInteger(value))) {
        setError(`${label} must be a positive ${key === 'monthlyBudgetUsd' ? 'number' : 'integer'}.`);
        return;
      }
      settings[key] = value;
    }
    if (!['all', 'pilot'].includes(settings.llmCustomerScope)) {
      setError('Choose a supported customer scope.');
      return;
    }
    const cohort = settings.llmPilotCustomerIds.trim()
      ? settings.llmPilotCustomerIds.trim().split(/[\s,]+/) : [];
    if (cohort.some(id => !/^[a-zA-Z0-9_-]{1,128}$/.test(id)) || new Set(cohort).size !== cohort.length) {
      setError('Approved customer IDs must be unique IDs using letters, numbers, underscores or hyphens (maximum 128 characters).');
      return;
    }
    delete settings.llmPilotCustomerIds;
    if (JSON.stringify(cohort) !== JSON.stringify(data.settings.llmPilotCustomerIds || [])) {
      settings.llmPilotCustomerIds = cohort;
    }
    const nightlyLimit = Number(settings.llmNightlyMaxCustomers);
    if (settings.llmNightlyMaxCustomers !== '' &&
        (!Number.isInteger(nightlyLimit) || nightlyLimit < 1 || nightlyLimit > 100)) {
      setError('Nightly customer limit must be an integer from 1-100.');
      return;
    }
    delete settings.llmNightlyMaxCustomers;
    if (draft.settings.llmNightlyMaxCustomers !== '' && nightlyLimit !== data.settings.llmNightlyMaxCustomers) {
      settings.llmNightlyMaxCustomers = nightlyLimit;
    }
    if (settings.llmNightlyEnabled && (!cohort.length || !Number.isInteger(nightlyLimit) || nightlyLimit < 1 || nightlyLimit > 100)) {
      setError('Before enabling nightly AI, enter approved customer IDs and a nightly customer limit from 1-100.');
      return;
    }
    if (draft.addons[ADDONS.AI_SALES_AGENT_PAID] && !draft.addons[ADDONS.AI_SALES_AGENT]) {
      setError('Enable the AI Sales Agent add-on before enabling paid summaries.');
      return;
    }
    if ((settings.llmEnabled || settings.llmNightlyEnabled) && !draft.addons[ADDONS.AI_SALES_AGENT_PAID]) {
      setError('Paid generation requires the paid AI summaries add-on.');
      return;
    }
    const addonChanges = Object.fromEntries(
      ADDON_CATALOG.filter(addon => draft.addons[addon.key] !== isAddonEnabled(data.addons, addon.key))
        .map(addon => [addon.key, draft.addons[addon.key]])
    );
    const request = revision.current;
    const saveLifecycle = lifecycle.current;
    setSaving(true);
    try {
      const result = await httpsCallable(functions, 'updatePlatformControls')({
        reason: reason.trim(),
        ...(Object.keys(addonChanges).length ? { addons: addonChanges } : {}),
        settings
      });
      if (request !== revision.current) return;
      if (result.data?.updated !== true) throw new Error('The server did not confirm the update.');
      setReason('');
      setConfirmed(false);
      setNotice('Platform controls saved.');
      await load();
    } catch (err) {
      if (request === revision.current) setError(message(err));
    } finally {
      if (saveLifecycle === lifecycle.current) setSaving(false);
    }
  };

  const submitTemporary = async (temporaryDailyTokenCap) => {
    if (!isOwner || saving || !draft) return;
    setError('');
    setNotice('');
    if (!reason.trim() || !confirmed) {
      setError('A change reason and explicit paid-cost confirmation are required for every save.');
      return;
    }
    if (temporaryDailyTokenCap && (!Number.isInteger(temporaryDailyTokenCap.tokens) || temporaryDailyTokenCap.tokens <= 0 ||
        !Number.isInteger(temporaryDailyTokenCap.hours) || temporaryDailyTokenCap.hours < 1 || temporaryDailyTokenCap.hours > 72)) {
      setError('Temporary cap needs a positive whole number of tokens and 1-72 hours.');
      return;
    }
    const request = revision.current;
    const saveLifecycle = lifecycle.current;
    setSaving(true);
    try {
      const result = await httpsCallable(functions, 'updatePlatformControls')({
        reason: reason.trim(), temporaryDailyTokenCap
      });
      if (request !== revision.current) return;
      if (result.data?.updated !== true) throw new Error('The server did not confirm the update.');
      setNotice(temporaryDailyTokenCap ? 'Temporary daily token cap applied.' : 'Temporary daily token cap cleared.');
      await load();
    } catch (err) {
      if (request === revision.current) setError(message(err));
    } finally {
      if (saveLifecycle === lifecycle.current) setSaving(false);
    }
  };

  const saveTemporary = event => {
    event.preventDefault();
    submitTemporary({ tokens: Number(tempTokens), hours: Number(tempHours) });
  };

  if (ownerLoading) return <p role="status" className="p-6">Checking platform-owner access...</p>;
  if (ownerError) return <p role="alert" className="p-6">Cannot verify platform-owner access: {message(ownerError)}</p>;
  if (!isOwner) return <p role="alert" className="p-6">Access denied. Platform-owner access is required.</p>;

  return (
    <main className="p-4 sm:p-6 space-y-5 min-w-0">
      <header>
        <h1 className="text-2xl font-bold">Platform Controls</h1>
        <p>Provider-only tenant management. Access is verified by your signed Firebase token.</p>
        {data && <p>Tenant: {data.tenantId}</p>}
        <button type="button" title="Reload platform controls" onClick={load} disabled={loading || saving}
          className="mt-2 border rounded px-3 py-2 disabled:opacity-50">Refresh controls</button>
      </header>
      {loading && <p role="status">Loading platform controls...</p>}
      {error && <p role="alert" className="text-red-700">{error}</p>}
      {notice && <p role="status" className="text-green-800">{notice}</p>}
      {data && draft && (
        <>
          <section aria-label="AI utilization" className="space-y-2">
            <h2 className="text-xl font-semibold">Budget and utilization</h2>
            <p>Month: {data.budget.monthKey}; day: {data.budget.dayKey} (UTC).</p>
            <p>Daily tokens reset at 00:00 UTC. Monthly budget resets on the first day of each month at 00:00 UTC.</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <Utilization label="Monthly spend" money used={data.budget.spentUsd} reserved={data.budget.reservedUsd}
                limit={data.settings.monthlyBudgetUsd} />
              <Utilization label="Daily tokens" used={data.budget.tokensUsedToday} reserved={data.budget.tokensReservedToday}
                limit={data.budget.effectiveDailyTokenCap ?? data.settings.dailyTokenCap} />
            </div>
            {data.budget.dailyTokenCapTemporary && (
              <p className="text-amber-900" role="status">
                Temporary daily token cap {count(data.budget.effectiveDailyTokenCap)} is active until{' '}
                {new Date(data.budget.dailyTokenCapOverrideExpiresAt).toLocaleString()}; the permanent cap of{' '}
                {count(data.settings.dailyTokenCap)} resumes automatically.
              </p>
            )}
            <p>Active reservations: {count(data.budget.reservationCount)}</p>
          </section>
          <form onSubmit={save} className="space-y-4">
            <fieldset disabled={saving || loading} className="space-y-4">
              <legend className="text-xl font-semibold">Tenant configuration</legend>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {ADDON_CATALOG.map(addon => (
                  <label key={addon.key} className="border rounded-lg p-3">
                    <input type="checkbox" aria-label={addon.name} checked={draft.addons[addon.key]}
                      onChange={event => toggleAddon(addon.key, event.target.checked)} />
                    <span className="ml-2 font-semibold">{addon.name}</span>
                    <p className="text-sm text-gray-600">{addon.description}</p>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {LIMITS.map(([key, label, step]) => (
                  <label key={key} className="flex flex-col min-w-0">{label}
                    <input type="number" required min={step} step={step} value={draft.settings[key] ?? ''}
                      onChange={event => setSetting(key, event.target.value)} className="border rounded p-2" />
                  </label>
                ))}
              </div>
              <label className="block">
                <input type="checkbox" checked={draft.settings.decisionBriefEnabled === true}
                  onChange={event => setSetting('decisionBriefEnabled', event.target.checked)} /> Enable rules-based decision brief (Phase 4 preview)
                <span className="block text-sm text-gray-600">Default off. Highlights an existing recommendation with evidence and one question; no paid AI calls.</span>
              </label>
              <label className="block">
                <input type="checkbox" checked={draft.settings.qualificationEnabled === true}
                  onChange={event => setSetting('qualificationEnabled', event.target.checked)} /> Enable guided opportunity qualification (Phase 4 preview)
                <span className="block text-sm text-gray-600">Default off. Shows a qualification card on opportunities with one focused question and reviewed answers; no paid AI calls.</span>
              </label>
              <label className="block">
                <input type="checkbox" checked={draft.settings.quotingEnabled === true}
                  onChange={event => setSetting('quotingEnabled', event.target.checked)} /> Enable quote builder (Phase 6)
                <span className="block text-sm text-gray-600">Default off. Reps build editable quote drafts from the synced product catalog and order history; rules only, no paid AI calls.</span>
              </label>
              <label className="block">
                <input type="checkbox" checked={draft.settings.quoteAiEnabled === true}
                  disabled={!draft.settings.quotingEnabled || !draft.settings.llmEnabled}
                  onChange={event => setSetting('quoteAiEnabled', event.target.checked)} /> Enable paid AI quote ranking
                <span className="block text-sm text-gray-600">Requires the quote builder and paid LLM generation. Ranks and explains quote lines, writes a customer summary and flags conflicts; counts toward the monthly budget and daily token cap.</span>
              </label>
              <label className="block">
                <input type="checkbox" checked={draft.settings.llmEnabled === true}
                  disabled={draft.addons[ADDONS.AI_SALES_AGENT_PAID] && !isAddonEnabled(data.addons, ADDONS.AI_SALES_AGENT_PAID)}
                  onChange={event => setSetting('llmEnabled', event.target.checked)} /> Enable paid LLM generation
              </label>
              <label className="block">
                <input type="checkbox" checked={draft.settings.llmNightlyEnabled === true}
                  disabled={!draft.settings.llmEnabled || (draft.addons[ADDONS.AI_SALES_AGENT_PAID] && !isAddonEnabled(data.addons, ADDONS.AI_SALES_AGENT_PAID))}
                  onChange={event => setSetting('llmNightlyEnabled', event.target.checked)} /> Enable nightly paid generation
              </label>
              <div className="space-y-3 border rounded-lg p-4">
                <h2 className="text-lg font-semibold">Approved customer cohort and nightly limits</h2>
                <p id="cohort-help" className="text-sm text-gray-600">
                  Nightly generation always uses this explicit list, even when on-demand scope is all permitted customers.
                  Enter customer IDs, not names, separated by commas or new lines. Saving this list approves those customers for paid AI.
                </p>
                <label className="block">Approved customer IDs
                  <textarea aria-describedby="cohort-help" rows={3} value={draft.settings.llmPilotCustomerIds}
                    onChange={event => setSetting('llmPilotCustomerIds', event.target.value)}
                    className="block w-full border rounded p-2" />
                </label>
                <label className="block">Maximum customers per nightly run (1-100)
                  <input type="number" min="1" max="100" step="1" value={draft.settings.llmNightlyMaxCustomers}
                    onChange={event => setSetting('llmNightlyMaxCustomers', event.target.value)}
                    className="block border rounded p-2" />
                </label>
                <p className="text-sm text-gray-600">
                  Scheduled at 4:00 AM America/New_York. Processes the first approved IDs in list order, up to the run limit,
                  and stops when the AI budget or daily token allowance blocks generation. Saving does not run AI immediately.
                  This list also controls on-demand access when pilot scope is selected.
                </p>
              </div>
              <div className="space-y-3 border rounded-lg p-4">
                <h2 className="text-lg font-semibold">Rolling summary refresh (active customers)</h2>
                <p className="text-sm text-gray-600">
                  Runs inside the 4:00 AM America/New_York job after any approved-list run. Each night it refreshes the
                  active customers whose AI summary is oldest, about one cycle&apos;s share at a time, so every active
                  customer is refreshed roughly once per cycle. It stops at the nightly limit, its share of the daily token
                  cap, the monthly budget, or after repeated provider errors; skipped customers carry over to the next night.
                  The remaining share of the daily cap stays available for on-demand refreshes.
                </p>
                <label className="block">
                  <input type="checkbox" checked={draft.settings.llmRollingEnabled === true}
                    disabled={!draft.settings.llmEnabled || (draft.addons[ADDONS.AI_SALES_AGENT_PAID] && !isAddonEnabled(data.addons, ADDONS.AI_SALES_AGENT_PAID))}
                    onChange={event => setSetting('llmRollingEnabled', event.target.checked)} /> Enable rolling refresh
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex flex-col min-w-0">Refresh cycle
                    <select className="border rounded p-2" value={draft.settings.llmRollingCycleDays ?? ''}
                      onChange={event => setSetting('llmRollingCycleDays', event.target.value)}>
                      <option value="" disabled>Select cycle</option>
                      {CYCLE_OPTIONS.map(([days, label]) => <option key={days} value={days}>{label}</option>)}
                    </select>
                  </label>
                  {ROLLING_FIELDS.map(([key, label, min, max]) => (
                    <label key={key} className="flex flex-col min-w-0">{label}
                      <input type="number" min={min} max={max} step="1" value={draft.settings[key] ?? ''}
                        onChange={event => setSetting(key, event.target.value)} className="border rounded p-2" />
                    </label>
                  ))}
                </div>
                {data.rolling && <RollingStatus rolling={data.rolling} />}
              </div>
              <label className="block">Paid customer scope
                <select className="ml-2 border rounded p-2" value={draft.settings.llmCustomerScope ?? ''}
                  disabled={draft.addons[ADDONS.AI_SALES_AGENT_PAID] && !isAddonEnabled(data.addons, ADDONS.AI_SALES_AGENT_PAID)}
                  onChange={event => setSetting('llmCustomerScope', event.target.value)} required>
                  <option value="" disabled>Select scope</option>
                  <option value="pilot">Configured pilot customers</option>
                  <option value="all">All permitted customers</option>
                </select>
              </label>
              <p className="text-sm text-amber-900">Enabling the paid add-on sets paid generation on, scope to all permitted customers, and nightly and rolling generation off. Calls already in progress may finish after disabling.</p>
              <label className="block">Change reason (required for each save)
                <textarea required maxLength={500} value={reason} onChange={event => setReason(event.target.value)}
                  className="block w-full border rounded p-2" />
              </label>
              <label className="block">
                <input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />
                {' '}I understand paid AI provider calls can incur charges and confirm these changes.
              </label>
              <button type="submit" title="Save all platform controls together" disabled={!reason.trim() || !confirmed}
                className="bg-indigo-600 text-white rounded px-4 py-2 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save platform controls'}
              </button>
            </fieldset>
          </form>
          <form onSubmit={saveTemporary} className="space-y-3 border rounded-lg p-4">
            <fieldset disabled={saving || loading} className="space-y-3">
              <legend className="text-xl font-semibold">Temporary daily token cap</legend>
              <p className="text-sm text-gray-600">For testing. Overrides the permanent daily cap until it expires, then reverts automatically. Uses the change reason and confirmation above.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col min-w-0">Temporary tokens per day
                  <input type="number" min="1" step="1" value={tempTokens}
                    onChange={event => setTempTokens(event.target.value)} className="border rounded p-2" />
                </label>
                <label className="flex flex-col min-w-0">Expires after (hours, 1-72)
                  <input type="number" min="1" max="72" step="1" value={tempHours}
                    onChange={event => setTempHours(event.target.value)} className="border rounded p-2" />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="submit" title="Apply a temporary daily token cap"
                  disabled={!reason.trim() || !confirmed}
                  className="bg-indigo-600 text-white rounded px-4 py-2 disabled:opacity-50">Apply temporary cap</button>
                <button type="button" title="Remove the temporary cap and use the permanent cap now"
                  disabled={!reason.trim() || !confirmed || !data.settings.dailyTokenCapOverride}
                  onClick={() => submitTemporary(null)}
                  className="border rounded px-4 py-2 disabled:opacity-50">Clear temporary cap</button>
              </div>
            </fieldset>
          </form>
          <section className="space-y-2">
            <h2 className="text-xl font-semibold">Model and pricing (read-only)</h2>
            <p>Current model: {data.settings.defaultModel || 'Not configured'}</p>
            {!Object.keys(data.pricing.models || {}).length && <p>Model pricing unavailable.</p>}
            {Object.entries(data.pricing.models || {}).map(([model, price]) => (
              <p key={model}>{model}: input {usd(price.inputPerMTok)} / million tokens; output {usd(price.outputPerMTok)} / million tokens.</p>
            ))}
          </section>
          <section className="space-y-2">
            <h2 className="text-xl font-semibold">Recent AI activity and errors</h2>
            {!data.recentUsage.length && <p>No recent AI usage.</p>}
            {data.recentUsage.map(entry => (
              <article key={entry.id} className="border rounded p-3 break-words">
                <p title={entry.userId ? `User ID: ${entry.userId}` : undefined}>
                  {entry.displayName || 'Unknown user'} — {entry.status}; customer: {entry.customerId || 'Unavailable'}
                </p>
                <details><summary>Record ID</summary>{entry.id}</details>
                <p><time>{entry.createdAt || 'Time unavailable'}</time></p>
                <p>Cost: {usd(entry.costUsd)}{entry.costEstimated ? ' (estimated)' : ''}; billed tokens: {count(entry.billedTokens)}</p>
                {entry.errorCode && <p className="text-red-700">Error: {entry.errorCode}</p>}
              </article>
            ))}
          </section>
          <section className="space-y-2">
            <h2 className="text-xl font-semibold">Provider audit trail</h2>
            {!data.recentAudit.length && <p>No recent provider changes.</p>}
            {data.recentAudit.map(entry => (
              <article key={entry.id} className="border rounded p-3 break-words">
                <p title={entry.actorUid ? `User ID: ${entry.actorUid}` : undefined}>
                  {entry.action} — {entry.actorName || 'Unknown user'}
                </p>
                <p><time>{entry.createdAt || 'Time unavailable'}</time></p>
                <p>Reason: {entry.reason}</p>
              </article>
            ))}
          </section>
        </>
      )}
    </main>
  );
}
